import { CdkDrag, CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TodoItem, TodoListBlock, TodoSection } from '../../todo.models';

@Component({
  selector: 'app-todo-list-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './todo-list-page.component.html',
  styleUrl: './todo-list-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TodoListPageComponent implements OnChanges {
  @Input() listId: number | null = null;
  @Input() listName = 'Todo list';
  @Input() remainingCount = 0;
  @Input() canWrite = true;
  @Input() isLoading = false;
  @Input() blocks: TodoListBlock[] = [];

  @Output() toggleTodo = new EventEmitter<TodoItem>();
  @Output() removeTodo = new EventEmitter<TodoItem>();
  @Output() renameTodo = new EventEmitter<{ todo: TodoItem; title: string }>();
  @Output() reorderBlocks = new EventEmitter<TodoListBlock[]>();
  @Output() createSection = new EventEmitter<string>();
  @Output() renameSection = new EventEmitter<{ sectionId: number; title: string }>();
  @Output() deleteSection = new EventEmitter<number>();

  viewMode: 'all' | 'active' | 'split' = 'all';
  localBlocks: TodoListBlock[] = [];
  visibleBlocks: TodoListBlock[] = [];
  todoBlocks: TodoListBlock[] = [];
  doneBlocks: TodoListBlock[] = [];
  collapsedSectionIds = new Set<number>();
  private swipedRowKey: string | null = null;
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeTrackingKey: string | null = null;
  contextMenu:
    | {
        x: number;
        y: number;
        type: 'item' | 'section';
        item?: TodoItem;
        section?: TodoSection;
      }
    | null = null;
  editItemTarget: TodoItem | null = null;
  editItemTitle = '';
  readonly rootDropListId = 'todo-root-drop-list';
  readonly viewModeCycle: Array<'all' | 'active' | 'split'> = ['all', 'active', 'split'];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listId']) {
      this.loadViewMode();
      if (!changes['blocks']) {
        this.localBlocks = [];
        this.refreshViewBlocks();
      }
    }

    if (changes['blocks']) {
      this.localBlocks = this.cloneBlocks(this.blocks);
      const sectionIds = new Set(this.localBlocks.filter(this.isSection).map((section) => section.id));
      this.collapsedSectionIds = new Set([...this.collapsedSectionIds].filter((id) => sectionIds.has(id)));
      this.refreshViewBlocks();
    }
  }

  get isSplitMode(): boolean {
    return this.viewMode === 'split';
  }

  get isReorderEnabled(): boolean {
    return this.viewMode === 'all' && this.canWrite;
  }

  get viewModeLabel(): string {
    switch (this.viewMode) {
      case 'active':
        return 'Hide done items';
      case 'split':
        return 'Split todo and done';
      default:
        return 'Show all items';
    }
  }

  get viewModeIcon(): string {
    switch (this.viewMode) {
      case 'active':
        return 'visibility_off';
      case 'split':
        return 'view_agenda';
      default:
        return 'list';
    }
  }

  onToggleViewMode(): void {
    const index = this.viewModeCycle.indexOf(this.viewMode);
    const nextIndex = index === -1 ? 0 : (index + 1) % this.viewModeCycle.length;
    this.viewMode = this.viewModeCycle[nextIndex];
    this.saveViewMode();
    this.refreshViewBlocks();
    this.clearSwipe();
  }

  onToggle(todo: TodoItem, event?: Event): void {
    if (event && this.isFromDragHandle(event.target)) {
      return;
    }

    this.clearSwipe();
    this.toggleTodo.emit(todo);
  }

  onRemove(todo: TodoItem, event: Event): void {
    event.stopPropagation();
    this.clearSwipe();
    this.removeTodo.emit(todo);
  }

  onCreateSection(): void {
    if (!this.canWrite) {
      return;
    }

    const title = window.prompt('Section title');
    const nextTitle = title?.trim();
    if (!nextTitle) {
      return;
    }

    this.createSection.emit(nextTitle);
  }

  onRenameSection(section: TodoSection, event: Event): void {
    event.stopPropagation();
    if (!this.canWrite) {
      return;
    }

    const title = window.prompt('Rename section', section.title);
    const nextTitle = title?.trim();
    if (!nextTitle || nextTitle === section.title) {
      return;
    }

    this.renameSection.emit({ sectionId: section.id, title: nextTitle });
    this.clearSwipe();
  }

  onDeleteSection(section: TodoSection, event: Event): void {
    event.stopPropagation();
    if (!this.canWrite) {
      return;
    }

    const confirmed = window.confirm(`Delete section "${section.title}"?`);
    if (!confirmed) {
      return;
    }

    this.deleteSection.emit(section.id);
    this.clearSwipe();
  }

  onRootDrop(event: CdkDragDrop<TodoListBlock[]>): void {
    if (!this.isReorderEnabled) {
      return;
    }

    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) {
        return;
      }
      moveItemInArray(this.localBlocks, event.previousIndex, event.currentIndex);
      this.emitReorder();
      return;
    }

    const moved = event.previousContainer.data[event.previousIndex];
    if (!this.isTodoItem(moved)) {
      return;
    }

    event.previousContainer.data.splice(event.previousIndex, 1);
    this.localBlocks.splice(event.currentIndex, 0, moved);
    this.emitReorder();
  }

  onSectionDrop(event: CdkDragDrop<TodoItem[]>, section: TodoSection): void {
    if (!this.isReorderEnabled) {
      return;
    }

    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) {
        return;
      }
      moveItemInArray(section.items, event.previousIndex, event.currentIndex);
      this.emitReorder();
      return;
    }

    const moved = event.previousContainer.data[event.previousIndex];
    if (!this.isTodoItem(moved)) {
      return;
    }

    if (event.previousContainer.id === this.rootDropListId) {
      event.previousContainer.data.splice(event.previousIndex, 1);
      section.items.splice(event.currentIndex, 0, moved);
      this.emitReorder();
      return;
    }

    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    this.emitReorder();
  }

  getConnectedDropListIds(listKey: 'main' | 'filtered' | 'todo' | 'done', blocks: TodoListBlock[]): string[] {
    if (!this.isReorderEnabled || listKey !== 'main') {
      return [];
    }

    return [
      this.getRootDropListId(listKey),
      ...blocks.filter(this.isSection).map((section) => this.getSectionDropListId(section.id, listKey))
    ];
  }

  getRootDropListId(listKey: 'main' | 'filtered' | 'todo' | 'done'): string {
    if (listKey === 'main') {
      return this.rootDropListId;
    }
    return `todo-root-drop-list-${listKey}`;
  }

  getSectionDropListId(sectionId: number, listKey: 'main' | 'filtered' | 'todo' | 'done'): string {
    return `todo-section-drop-list-${sectionId}-${listKey}`;
  }

  canEnterSectionList = (drag: CdkDrag<unknown>): boolean => {
    return this.isTodoItem(drag.data);
  };

  trackBlock(_: number, block: TodoListBlock): string {
    return block.type === 'section' ? `section-${block.id}` : `item-${block.id}`;
  }

  trackItem(_: number, item: TodoItem): number {
    return item.id;
  }

  isSectionBlock(block: TodoListBlock): block is TodoSection {
    return block.type === 'section';
  }

  isSectionCollapsed(sectionId: number): boolean {
    return this.collapsedSectionIds.has(sectionId);
  }

  sectionCounts(section: TodoSection): { done: number; total: number } {
    const total = section.items.length;
    const done = section.items.reduce((count, item) => count + (item.completed ? 1 : 0), 0);
    return { done, total };
  }

  toggleSection(sectionId: number, event?: Event): void {
    event?.stopPropagation();
    if (this.collapsedSectionIds.has(sectionId)) {
      this.collapsedSectionIds.delete(sectionId);
      return;
    }

    this.collapsedSectionIds.add(sectionId);
  }

  onRowTouchStart(event: TouchEvent, rowKey: string): void {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    this.swipeStartX = touch.clientX;
    this.swipeStartY = touch.clientY;
    this.swipeTrackingKey = rowKey;
  }

  onRowTouchMove(event: TouchEvent, rowKey: string): void {
    if (this.swipeTrackingKey !== rowKey) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (this.isFromDragHandle(event.target)) {
      return;
    }

    const deltaX = touch.clientX - this.swipeStartX;
    const deltaY = touch.clientY - this.swipeStartY;

    // Ignore vertical scroll gestures.
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      return;
    }

    if (deltaX < -36) {
      this.swipedRowKey = rowKey;
      return;
    }

    if (deltaX > 24 && this.swipedRowKey === rowKey) {
      this.clearSwipe();
    }
  }

  onRowTouchEnd(): void {
    this.swipeTrackingKey = null;
  }

  onItemContextMenu(event: MouseEvent, item: TodoItem): void {
    if (!this.canWrite) {
      return;
    }

    this.openContextMenu(event, { type: 'item', item });
  }

  onSectionContextMenu(event: MouseEvent, section: TodoSection): void {
    if (!this.canWrite) {
      return;
    }

    this.openContextMenu(event, { type: 'section', section });
  }

  onContextEdit(): void {
    if (!this.contextMenu) {
      return;
    }

    if (this.contextMenu.type === 'item' && this.contextMenu.item) {
      this.openEditItemDialog(this.contextMenu.item);
      this.closeContextMenu();
      return;
    }

    if (this.contextMenu.type === 'section' && this.contextMenu.section) {
      const nextTitle = window.prompt('Rename section', this.contextMenu.section.title)?.trim();
      if (nextTitle && nextTitle !== this.contextMenu.section.title) {
        this.renameSection.emit({ sectionId: this.contextMenu.section.id, title: nextTitle });
      }
      this.closeContextMenu();
    }
  }

  onContextDelete(): void {
    if (!this.contextMenu) {
      return;
    }

    if (this.contextMenu.type === 'item' && this.contextMenu.item) {
      this.removeTodo.emit(this.contextMenu.item);
      this.closeContextMenu();
      return;
    }

    if (this.contextMenu.type === 'section' && this.contextMenu.section) {
      const confirmed = window.confirm(`Delete section "${this.contextMenu.section.title}"?`);
      if (confirmed) {
        this.deleteSection.emit(this.contextMenu.section.id);
      }
      this.closeContextMenu();
    }
  }

  closeContextMenu(): void {
    this.contextMenu = null;
  }

  closeEditItemDialog(): void {
    this.editItemTarget = null;
    this.editItemTitle = '';
  }

  submitEditItemDialog(): void {
    if (!this.editItemTarget) {
      return;
    }

    const nextTitle = this.editItemTitle.trim();
    if (!nextTitle || nextTitle === this.editItemTarget.title) {
      this.closeEditItemDialog();
      return;
    }

    this.renameTodo.emit({ todo: this.editItemTarget, title: nextTitle });
    this.closeEditItemDialog();
  }

  onInlineEdit(todo: TodoItem, event: Event): void {
    event.stopPropagation();
    this.openEditItemDialog(todo);
  }

  isRowSwiped(rowKey: string): boolean {
    return this.swipedRowKey === rowKey;
  }

  itemRowKey(item: TodoItem): string {
    return `item-${item.id}`;
  }

  sectionRowKey(sectionId: number): string {
    return `section-${sectionId}`;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeContextMenu();
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.closeContextMenu();
  }

  private emitReorder(): void {
    this.reorderBlocks.emit(this.cloneBlocks(this.localBlocks));
  }

  private refreshViewBlocks(): void {
    if (this.viewMode === 'all') {
      this.visibleBlocks = this.localBlocks;
      this.todoBlocks = [];
      this.doneBlocks = [];
      return;
    }

    const activePredicate = (item: TodoItem): boolean => !item.completed;
    const donePredicate = (item: TodoItem): boolean => item.completed;

    if (this.viewMode === 'active') {
      this.visibleBlocks = this.filterBlocks(activePredicate);
      this.todoBlocks = [];
      this.doneBlocks = [];
      return;
    }

    this.visibleBlocks = [];
    this.todoBlocks = this.filterBlocks(activePredicate);
    this.doneBlocks = this.filterBlocks(donePredicate);
  }

  private loadViewMode(): void {
    if (!this.listId) {
      this.viewMode = 'all';
      return;
    }

    try {
      const raw = window.localStorage.getItem(this.getViewModeStorageKey(this.listId));
      if (raw === 'all' || raw === 'active' || raw === 'split') {
        this.viewMode = raw;
        return;
      }
    } catch {
      // ignore storage errors and fall back to default
    }

    this.viewMode = 'all';
  }

  private saveViewMode(): void {
    if (!this.listId) {
      return;
    }

    try {
      window.localStorage.setItem(this.getViewModeStorageKey(this.listId), this.viewMode);
    } catch {
      // ignore storage errors
    }
  }

  private getViewModeStorageKey(listId: number): string {
    return `todo.viewMode.${listId}`;
  }

  private filterBlocks(predicate: (item: TodoItem) => boolean): TodoListBlock[] {
    return this.localBlocks.reduce<TodoListBlock[]>((acc, block) => {
      if (block.type === 'item') {
        if (predicate(block)) {
          acc.push({ ...block });
        }
        return acc;
      }

      const items = block.items.filter(predicate).map((item) => ({ ...item }));
      if (!items.length) {
        return acc;
      }

      acc.push({ ...block, items });
      return acc;
    }, []);
  }

  private cloneBlocks(source: TodoListBlock[]): TodoListBlock[] {
    return source.map((block) => {
      if (block.type === 'item') {
        return { ...block };
      }

      return {
        ...block,
        items: block.items.map((item) => ({ ...item }))
      };
    });
  }

  private isSection = (block: TodoListBlock): block is TodoSection => {
    return block.type === 'section';
  };

  private isTodoItem(value: unknown): value is TodoItem {
    return Boolean(value) && typeof value === 'object' && (value as TodoItem).type === 'item';
  }

  private clearSwipe(): void {
    this.swipedRowKey = null;
  }

  private openEditItemDialog(item: TodoItem): void {
    this.editItemTarget = item;
    this.editItemTitle = item.title;
  }

  private openContextMenu(
    event: MouseEvent,
    menu: {
      type: 'item' | 'section';
      item?: TodoItem;
      section?: TodoSection;
    }
  ): void {
    event.preventDefault();
    event.stopPropagation();

    const menuWidth = 176;
    const menuHeight = 96;
    const x = Math.min(event.clientX, window.innerWidth - menuWidth - 12);
    const y = Math.min(event.clientY, window.innerHeight - menuHeight - 12);

    this.contextMenu = {
      x: Math.max(8, x),
      y: Math.max(8, y),
      ...menu
    };
  }

  private isFromDragHandle(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return Boolean(target.closest('.drag-handle'));
  }
}
